import InlineBanner from '@/components/UI/InlineBanner/InlineBanner';
import { _isNotEmpty } from '@/littledash';
import type { SendCoercionWarning, SendFileDetection } from '@/model/Agent.model';
import type { FC } from 'react';

interface SendCoercionWarningsProps {
  detections: Array<SendFileDetection>;
  testId?: string;
}

interface SuspectFile {
  attachmentApiId: string;
  filename: string;
  warnings: Array<SendCoercionWarning>;
  omitted: number;
}

/** What the user should do about it, said once for the whole block rather than under every column. */
export const SEND_COERCION_REMEDY =
  'Validation will still run, but some findings may describe changes Excel made rather than your data. ' +
  'To fix it, re-export the files from the source system without opening them in Excel, or, if you have to ' +
  'use Excel, import every column as Text and keep dates as YYYY-MM-DD.';

/**
 * Said beside the warnings because the patterns' thresholds are our reading, awaiting a SEND practitioner's
 * review: a warning can be wrong, and the user should know that before acting on one.
 */
export const SEND_COERCION_PROVISIONAL =
  'These signs are our current reading and are still under expert review, so a warning can be wrong.';

/**
 * The files whose columns look rewritten by Excel, shown before the user confirms domains.
 *
 * A warning, not a failure: it sits in a flat warning banner beside the form, which stays usable, unlike
 * the plain list of input failures that replaces it when a run cannot start, and unlike the raised banners
 * conformance findings use. Renders nothing when no file has a warning, including a detection stored
 * before files were scanned, which carries no warnings at all.
 */
const SendCoercionWarnings: FC<SendCoercionWarningsProps> = ({ detections, testId = 'send-coercion-warnings' }) => {
  const suspect = detections.reduce<Array<SuspectFile>>((acc, detection) => {
    const warnings = detection.coercion_warnings ?? [];
    const omitted = detection.coercion_warnings_omitted ?? 0;
    if (_isNotEmpty(warnings) || omitted > 0) {
      acc.push({
        attachmentApiId: detection.attachment_api_id,
        filename: detection.original_filename,
        warnings,
        omitted,
      });
    }
    return acc;
  }, []);

  if (!_isNotEmpty(suspect)) {
    return null;
  }

  return (
    <div data-test-component="SendCoercionWarnings" className="mb3">
      <InlineBanner variant="warning" title="These files look like they were saved in Excel" testId={testId}>
        <p className="f6 mt1 mb2" data-test-element="remedy">
          {SEND_COERCION_REMEDY}
        </p>
        <p className="f6 mt0 mb2 mid-gray" data-test-element="provisional">
          {SEND_COERCION_PROVISIONAL}
        </p>
        <ul className="list pl0 ma0" data-test-element="files">
          {suspect.map((file) => (
            <li
              key={file.attachmentApiId}
              className="mt2"
              data-test-element="file"
              data-test-key={file.attachmentApiId}
            >
              <div className="f6 b" data-test-element="file-name">
                {file.filename}
              </div>
              <ul className="list pl0 ma0">
                {file.warnings.map((warning) => (
                  <li
                    key={`${warning.column_name}-${warning.kind}`}
                    className="f6 mt1"
                    data-test-element="warning"
                    data-test-key={`${warning.column_name}-${warning.kind}`}
                  >
                    <span data-test-element="warning-message">{warning.message}</span>{' '}
                    <span className="mid-gray" data-test-element="warning-samples">
                      (e.g. {warning.samples.join(', ')})
                    </span>
                  </li>
                ))}
                {file.omitted > 0 && (
                  <li className="f6 mt1 mid-gray" data-test-element="omitted">
                    …and {file.omitted.toLocaleString('en-US')} more {file.omitted === 1 ? 'warning' : 'warnings'} for
                    this file.
                  </li>
                )}
              </ul>
            </li>
          ))}
        </ul>
      </InlineBanner>
    </div>
  );
};

export default SendCoercionWarnings;
